import React from 'react';
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Trophy, ChevronRight } from "lucide-react";

interface MatchData {
  id: number;
  matchNumber: number;
  player1Id: number;
  player2Id: number;
  winnerId: number | null;
  round: number;
  status: string;
  gameRoomId?: number;
}

interface Player {
  id: number;
  username: string;
}

interface TournamentBracketProps {
  matches: MatchData[];
  players: Player[];
  maxRounds: number;
  userId?: number;
  winnerUserId?: number | null;
  isCreator?: boolean;
  onEditWinner?: (matchId: number, player1Id: number, player2Id: number) => void;
}

export function TournamentBracket({ 
  matches, 
  players, 
  maxRounds, 
  userId, 
  winnerUserId,
  isCreator = false,
  onEditWinner
}: TournamentBracketProps) {
  // Organize matches by round
  const matchesByRound = matches.reduce((acc, match) => {
    if (!acc[match.round]) {
      acc[match.round] = [];
    }
    acc[match.round].push(match);
    return acc;
  }, {} as Record<number, MatchData[]>);

  // Helper function to get player name
  const getPlayerName = (playerId: number) => {
    if (playerId === userId) return "You";
    const player = players.find(p => p.id === playerId);
    return player ? player.username : `Player #${playerId}`;
  };

  // Calculate match connections between rounds
  const getMatchConnections = (currentRound: number) => {
    if (currentRound >= maxRounds) return [];
    
    const currentMatches = matchesByRound[currentRound] || [];
    const nextRoundMatches = matchesByRound[currentRound + 1] || [];
    
    return currentMatches.map((match) => {
      if (!match.winnerId) return null;
      
      // Find the next round match this player advances to
      const nextMatch = nextRoundMatches.find(
        (nextMatch) => 
          nextMatch.player1Id === match.winnerId || 
          nextMatch.player2Id === match.winnerId
      );
      
      return nextMatch ? { 
        fromMatch: match.id, 
        toMatch: nextMatch.id,
        winnerId: match.winnerId 
      } : null;
    }).filter(Boolean);
  };

  // Calculate spacing between matches based on round
  const getMatchSpacing = (round: number) => {
    const baseSpacing = 6; // Reduced base spacing in rem
    // Use a more gentle increase for early rounds
    return round === 1 ? baseSpacing : baseSpacing * Math.pow(1.5, round - 1);
  };

  return (
    <div className="my-6 overflow-auto pb-4 max-w-full">
      <div className="flex flex-row-reverse justify-center items-start gap-3 lg:gap-5 min-w-[600px]">
        {/* We reverse the order of rounds to make final appear on the right side */}
        {Array.from({ length: maxRounds }, (_, i) => maxRounds - i).map((round) => {
          const roundMatches = matchesByRound[round] || [];
          const matchConnections = getMatchConnections(round);
          const spacing = getMatchSpacing(round);
          
          return (
            <div key={round} className="flex flex-col relative">
              <div className="text-center font-medium mb-3 px-2 py-1 bg-primary/10 rounded-md text-sm">
                {round === 1 ? "First Round" : 
                 round === maxRounds ? "Final" : 
                 `Round ${round}`}
                <span className="ml-1 text-xs opacity-60">({roundMatches.length})</span>
              </div>

              <div 
                className="flex flex-col" 
                style={{ 
                  gap: `${spacing}rem`,
                  marginTop: round > 1 ? `${(round - 1) * 2}rem` : "0" 
                }}
              >
                {roundMatches.map((match, matchIndex) => {
                  // Special case for bye matches where both players are the same
                  const isByeMatch = match.player1Id === match.player2Id;
                  
                  return (
                    <div key={match.id} className="relative bracket-match-container" data-match-id={match.id}>
                      <Card className={`theme-card overflow-hidden w-32 ${match.status === 'completed' ? 'border-primary/20' : ''}`}>
                        <CardContent className="p-2">
                          <div className="text-xs font-medium mb-1 flex justify-between items-center">
                            <span>Match #{match.matchNumber}</span>
                            {match.status === 'completed' && (
                              <Badge variant="default" className="text-[10px] px-1 py-0 h-4 text-white">
                                ✓
                              </Badge>
                            )}
                          </div>
                          
                          {isByeMatch ? (
                            <div className="flex items-center gap-1 py-1">
                              <Badge variant="outline" className="text-[10px] px-1 py-0 text-white">Bye</Badge>
                              <div className="text-xs font-bold">{getPlayerName(match.player1Id)}</div>
                            </div>
                          ) : (
                            <div className="flex flex-col gap-1">
                              <div className={`text-xs ${match.winnerId === match.player1Id ? "font-bold text-primary" : ""}`}>
                                {getPlayerName(match.player1Id)}
                              </div>
                              
                              <div className="text-xs theme-text-muted">vs</div>
                              
                              <div className={`text-xs ${match.winnerId === match.player2Id ? "font-bold text-primary" : ""}`}>
                                {getPlayerName(match.player2Id)}
                              </div>
                              
                              {/* Edit winner button for tournament creator */}
                              {isCreator && onEditWinner && match.status !== 'pending' && (
                                <button 
                                  onClick={() => onEditWinner(match.id, match.player1Id, match.player2Id)}
                                  className="mt-1 text-[10px] px-1 py-0.5 rounded bg-primary/10 text-primary hover:bg-primary/20 transition-colors text-center"
                                >
                                  Edit Winner
                                </button>
                              )}
                            </div>
                          )}
                        </CardContent>
                      </Card>
                      
                      {/* Show advancing arrow for winners */}
                      {match.winnerId && round > 1 && (
                        <div className="absolute left-0 top-1/2 w-6 flex items-center justify-center" style={{ transform: "translateY(-50%) rotate(180deg)" }}>
                          <ChevronRight className="h-4 w-4 text-primary/50" />
                        </div>
                      )}
                      
                      {/* Connection lines to next round */}
                      {match.winnerId && round > 1 && (
                        <div className="absolute left-0 top-1/2 w-4 border-t border-primary/40" style={{ transform: "translateY(-50%)" }} />
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}

        {/* Tournament winner trophy - now positioned at the beginning of the flex container */}
        {winnerUserId && (
          <div className="flex flex-col items-center justify-center mt-16 order-first">
            <div className="h-12 w-12 rounded-full bg-primary/20 flex items-center justify-center">
              <Trophy className="h-6 w-6 theme-text-primary" />
            </div>
            <div className="text-sm font-bold mt-2">
              {winnerUserId === userId ? "You Won!" : getPlayerName(winnerUserId)}
            </div>
            <div className="text-xs opacity-70">Champion</div>
          </div>
        )}
      </div>
    </div>
  );
}